/**
 * Unified data-risk score (doc 33, cross-cutting standard #2).
 *
 * Every list in Discovery ranks by ONE score so the highest-risk gaps sit at
 * the top - not by raw severity, which floods the operator with same-coloured
 * rows. The score correlates the four levers the 2026 best-in-class DSPMs use
 * (Wiz / Cyera / Sentra): sensitivity x exposure x access-breadth x activity,
 * with a DPDPA-class boost on top (children / sensitive-financial / health /
 * credential) because that is our India-first differentiator.
 *
 * Pure + deterministic so it is unit-tested (risk-score.test.ts) and reused by
 * Findings, Asset Inventory, Data Lineage, and the Dashboard.
 */

export type RiskBand = 'critical' | 'high' | 'medium' | 'low';

/** The minimal shape a risk score is computed from. Every Discovery finding
 *  already carries these (see Findings adaptDtoToFinding). */
export interface RiskInput {
  severity?: 'critical' | 'high' | 'medium' | 'low' | string;
  /** DPDPA class: children / sensitive_financial / sensitive_health /
   *  credential / personal_identifier / personal / device. */
  governanceCategory?: string;
  /** high | medium | low. */
  sensitivity?: string;
  /** 'review' | 'orphan_no_owner' | 'unauthorized' | null. */
  legitimacy?: string | null;
  /** Confidence tier: 'possible' (release 3.3 B4; 'review' before it) caps the score (uncertain match). */
  tier?: 'confirmed' | 'likely' | 'possible' | 'review' | string;
  /** Peak classifier confidence 0..1. */
  confidence?: number;
  /** Distinct affected rows (volume multiplier, log-scaled). */
  count?: number | null;
  /** Was recent access to this data observed (DAM signal). */
  activityObserved?: boolean;
  /** Does this finding answer a live breach. */
  isBreach?: boolean;
  /** Workflow status; resolved / false_positive deprioritise hard. */
  status?: string;
}

export interface RiskScore {
  /** 0-100, higher = act first. */
  score: number;
  band: RiskBand;
  /** The contributing factors, for the "why this rank" rationale (doc 33 #5). */
  factors: Array<{ label: string; points: number }>;
}

const SEVERITY_BASE: Record<string, number> = {
  critical: 64,
  high: 46,
  medium: 28,
  low: 12,
  // Below low, so an info finding never ranks above a low one (it was
  // missing, and an unknown severity scores as medium).
  info: 4,
};

const GOVERNANCE_BOOST: Record<string, { points: number; label: string }> = {
  children: { points: 20, label: 'DPDPA Section 9 (children)' },
  sensitive_financial: { points: 13, label: 'Sensitive financial' },
  sensitive_health: { points: 13, label: 'Sensitive health' },
  credential: { points: 16, label: 'Live credential / secret' },
  personal_identifier: { points: 9, label: 'Strong identifier' },
  personal: { points: 0, label: 'Personal data' },
  device: { points: 2, label: 'Device identifier' },
};

const EXPOSURE_BOOST: Record<string, { points: number; label: string }> = {
  unauthorized: { points: 14, label: 'Unauthorised access path' },
  orphan_no_owner: { points: 9, label: 'Orphaned (no data owner)' },
};

function bandFor(score: number): RiskBand {
  if (score >= 75) return 'critical';
  if (score >= 50) return 'high';
  if (score >= 25) return 'medium';
  return 'low';
}

/** Compute the unified 0-100 risk score + band + the factors behind it. */
export function riskScore(input: RiskInput): RiskScore {
  const factors: Array<{ label: string; points: number }> = [];
  const add = (label: string, points: number): void => {
    if (points !== 0) factors.push({ label, points: Math.round(points) });
  };

  // Resolved / dismissed findings are not "act now" work - floor them low so
  // they never crowd the top of a queue, but keep them rankable among each other.
  const status = input.status ?? 'open';
  if (status === 'resolved' || status === 'false_positive') {
    const residual = (SEVERITY_BASE[input.severity ?? 'low'] ?? 12) * 0.12;
    return {
      score: Math.round(residual),
      band: bandFor(residual),
      factors: [{ label: status === 'resolved' ? 'Resolved' : 'Marked false positive', points: 0 }],
    };
  }

  let score = SEVERITY_BASE[input.severity ?? 'medium'] ?? 28;
  add(`Severity: ${input.severity ?? 'medium'}`, score);

  const dpdpa = GOVERNANCE_BOOST[input.governanceCategory ?? 'personal'];
  if (dpdpa) {
    score += dpdpa.points;
    add(dpdpa.label, dpdpa.points);
  }

  const exposure = input.legitimacy ? EXPOSURE_BOOST[input.legitimacy] : undefined;
  if (exposure) {
    score += exposure.points;
    add(exposure.label, exposure.points);
  }

  if (input.isBreach) {
    score += 10;
    add('Tied to a live breach', 10);
  }

  if (input.activityObserved) {
    score += 7;
    add('Recent access observed', 7);
  }

  // Volume: log-scaled so 10 rows and 10M rows differ but do not dwarf the
  // qualitative signal. ~+2 at 100 rows, ~+5 at 100k, capped at +8.
  const count = typeof input.count === 'number' && input.count > 0 ? input.count : 0;
  if (count > 0) {
    const vol = Math.min(8, Math.log10(count + 1) * 2.4);
    score += vol;
    add(`Volume: ${count.toLocaleString('en-IN')} records`, vol);
  }

  // Uncertainty: a "review"-tier or low-confidence match is downweighted so a
  // confirmed match always outranks an equal-severity unconfirmed one.
  if (input.tier === 'possible' || input.tier === 'review') {
    score *= 0.7;
    add('Possible (not in headline counts)', -0); // shown qualitatively, not as points
  } else if (typeof input.confidence === 'number' && input.confidence > 0 && input.confidence < 0.6) {
    score *= 0.6 + input.confidence * 0.4 / 0.6;
  }

  score = Math.max(0, Math.min(100, score));
  return { score: Math.round(score), band: bandFor(score), factors };
}

/** Comparator: highest risk first. Stable tiebreak by raw severity then count. */
export function byRiskDesc(a: RiskInput, b: RiskInput): number {
  const sa = riskScore(a).score;
  const sb = riskScore(b).score;
  if (sb !== sa) return sb - sa;
  const ca = typeof a.count === 'number' ? a.count : 0;
  const cb = typeof b.count === 'number' ? b.count : 0;
  return cb - ca;
}

/**
 * Roll up a risk-sorted list to the top-N DISTINCT items by key, with a count
 * of how many collapsed into each (doc 33 #5 - "do this next" must be N
 * DISTINCT actions, never the same finding N times). Input must already be
 * sorted highest-risk-first; the first occurrence of each key is kept.
 */
export function dedupeTopBy<T>(
  items: T[],
  keyFn: (t: T) => string,
  n: number,
): Array<{ item: T; count: number }> {
  const seen = new Map<string, { item: T; count: number }>();
  const order: string[] = [];
  for (const it of items) {
    const k = keyFn(it);
    const e = seen.get(k);
    if (e) e.count += 1;
    else {
      seen.set(k, { item: it, count: 1 });
      order.push(k);
    }
  }
  return order.slice(0, n).map((k) => seen.get(k)!);
}

const BAND_LABEL: Record<RiskBand, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export function riskBandLabel(band: RiskBand): string {
  return BAND_LABEL[band];
}
